import { ArrowRight, Lock } from "lucide-react";

interface Props {
  onSignup: () => void;
  emphasis?: "soft" | "primary";
}

const SignupConversionBlock = ({ onSignup, emphasis = "soft" }: Props) => {
  const isPrimary = emphasis === "primary";

  return (
    <section className="w-full max-w-2xl mx-auto mt-10 pt-8 border-t border-white/[0.07]">
      <div
        className={
          "rounded-2xl border p-5 md:p-7 transition-all " +
          (isPrimary
            ? "border-cyan-400/30 bg-gradient-to-b from-cyan-400/[0.07] to-transparent shadow-[0_0_60px_-20px_rgba(34,211,238,0.55)]"
            : "border-cyan-400/15 bg-gradient-to-b from-cyan-400/[0.03] to-transparent")
        }
      >
        <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-white leading-snug">
          Knowing what it is changes everything.{" "}
          <span className="text-cyan-300">Knowing what to say next changes the conversation.</span>
        </h2>
        <p className="mt-3 text-sm md:text-[15px] text-white/70 leading-relaxed">
          See the exact words that hold the line — without escalating, without apologizing
          for raising it.
        </p>

        {/* Locked reply preview — purely presentational, no user text */}
        <div className="mt-6 relative">
          <div
            aria-hidden
            className={
              "rounded-2xl border border-cyan-400/15 bg-white/[0.025] p-5 md:p-6 " +
              "relative overflow-hidden"
            }
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] uppercase tracking-[0.22em] text-cyan-300/80 font-mono">
                Calibrated Reply · Grey Rock
              </span>
              <span className="text-[10px] uppercase tracking-[0.18em] text-white/35 font-mono">
                Locked
              </span>
            </div>

            {/* Skeuomorph reply bubble with blurred placeholder lines */}
            <div className="relative">
              <div className="rounded-2xl rounded-tl-sm bg-white/[0.04] border border-white/[0.06] p-4 md:p-5 space-y-2.5">
                <div className="h-3 rounded bg-white/15 w-[92%] blur-[3px]" />
                <div className="h-3 rounded bg-white/15 w-[78%] blur-[3px]" />
                <div className="h-3 rounded bg-white/15 w-[85%] blur-[3px]" />
                <div className="h-3 rounded bg-white/15 w-[54%] blur-[3px]" />
              </div>

              {/* Redaction veil */}
              <div
                aria-hidden
                className="absolute inset-0 rounded-2xl pointer-events-none"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(0,0,0,0.0) 0%, rgba(0,0,0,0.35) 100%)",
                }}
              />

              {/* Padlock center */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-12 h-12 rounded-full bg-black/70 border border-cyan-400/40 flex items-center justify-center shadow-[0_0_30px_-4px_rgba(34,211,238,0.6)]">
                  <Lock className="w-5 h-5 text-cyan-300" />
                </div>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2 text-[11px] font-mono text-white/40 uppercase tracking-wider">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-cyan-300/80 animate-pulse" />
              Soft Neutral · Firm Boundary · Minimal Grey Rock
            </div>
          </div>
        </div>

        <button
          onClick={onSignup}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 h-12 px-6 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-black text-sm font-semibold tracking-wide transition-all shadow-[0_0_40px_-6px_rgba(34,211,238,0.85)] hover:shadow-[0_0_60px_-4px_rgba(34,211,238,1)]"
        >
          Create your free account to unlock this
          <ArrowRight className="w-4 h-4" />
        </button>

        <p className="mt-3 text-center text-[12px] text-white/50">
          No password required. You'll get 3 credits to start — your first response is one of them.
        </p>
      </div>
    </section>
  );
};

export default SignupConversionBlock;
